import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { DownloadInvUploadDetailRequest, GetInvUploadRequest, InvUploadColumnName, PagedInvUploadResponse } from '../pages/inv-upload-component/models/inv-upload.model';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { environment } from 'src/environments/environment';
import { ExcelFile } from 'src/app/core/models/excel-file';

/**
 * Service responsible for inventory upload API operations
 * 
 * Follows Single Responsibility Principle:
 * - Dedicated to handling HTTP communication with the inventory upload endpoint
 * - Abstracts API details from components
 * - Provides a clean interface for callers
 */
@Injectable({
  providedIn: 'root'
})
export class InvUploadService {

  private readonly apiBaseUrl = 'invUpload';

  constructor(private httpClient: HttpClient) { }

  /**
   * Retrieves paginated inventory upload records based on date range criteria
   * 
   * @param request - Contains date range filters (createDateStart and createDateEnd)
   * @param page - Zero-based page number (default: 0)
   * @param size - Number of records per page (default: 10)
   * @param sort - Sort criteria in format 'field,direction' e.g., 'createdAt,desc'
   * @returns Observable of paginated inventory upload responses
   * 
   * Example:
   * const request: GetInvUploadRequest = {
   *   createDateStart: new Date('2024-01-01'),
   *   createDateEnd: new Date('2024-12-31')
   * };
   * this.invUploadService.getInvUploads(request, 0, 20).subscribe(
   *   response => console.log(response),
   *   error => console.error(error)
   * );
   */
  getInvUploads(
    request: GetInvUploadRequest,
    page: number = 0,
    size: number = 5,
    sortOrder?: SortOrder
  ): Observable<PagedInvUploadResponse> {
    const sort = this.convertSortOrderToParam(sortOrder);
    const url = `${environment.logisticsApiUrl}/${this.apiBaseUrl}/get`;
    const params = this.buildHttpParams(request, page, size, sort);
    return this.httpClient.get<PagedInvUploadResponse>(
      url,
      { params }
    ).pipe(
      map(response => {

        if(response.content){
            response.content = response.content.map(item => ({
            ...item,
            createdAt: item.createdAt ? new Date(item.createdAt) : item.createdAt,
            updatedAt: item.updatedAt ? new Date(item.updatedAt) : item.updatedAt
          }));
        }

        return response;
      })
    );
  }

    /**
   * 上傳庫存批次異動 Excel 檔案
   * @param file 檔案物件
   */
  uploadExcel(file: File): Observable<void> {
    const url = `${environment.logisticsApiUrl}/${this.apiBaseUrl}/uploadInvUploadExcel`;
    const formData = new FormData();
    // 必須與後端 @RequestParam("file") 名稱一致
    formData.append('file', file);
    
    return this.httpClient.post<void>(url, formData);
  }

    /**
   * 下載範例 Excel
   * 使用 observe: 'response' 來獲取 Header 中的檔名
   */
  downloadSampleExcel(): Observable<ExcelFile> {
    const url = `${environment.logisticsApiUrl}/${this.apiBaseUrl}/downloadInvUploadSampleExcel`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((res: HttpResponse<Blob>) => {
        return {
          blob: res.body as Blob,
          fileName: this.extractFileName(res) || 'InvUpload_Sample.xlsx'
        };
      })
    );
  }

  downloadInvUploadDetailExcel(request: DownloadInvUploadDetailRequest): Observable<ExcelFile> {
    const url = `${environment.logisticsApiUrl}/${this.apiBaseUrl}/downloadInvUploadDetailExcel`;
    const params = new HttpParams().set(InvUploadColumnName.INV_UPLOAD_NO, request.invUploadNo);
    
    return this.httpClient.get(url, {
      params,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((res: HttpResponse<Blob>) => {
        return {
          blob: res.body as Blob,
          fileName: this.extractFileName(res) || `InvUpload_detail_${request.invUploadNo}.xlsx`
        };
      })
    );
  }

  /**
   * Converts a `SortOrder` object to the API `sort` query parameter.
   * - `direction` uses -1 for desc and 1 for asc
   * - falls back to `createdAt,desc` when missing
   */
  private convertSortOrderToParam(sortOrder?: SortOrder): string {
    const defaultSort = 'createdAt,desc';
    if (!sortOrder || !sortOrder.property) {
      return defaultSort;
    }

    const direction = sortOrder.direction === -1 ? 'desc' : 'asc';
    return `${sortOrder.property},${direction}`;
  }

  /**
   * Builds HTTP parameters from the request and pagination criteria
   * Filters out null values to keep the query string clean
   * 
   * @param request - The search criteria
   * @param page - Current page number
   * @param size - Page size
   * @param sort - Sort criteria
   * @returns HttpParams object ready for HTTP request
   */
  private buildHttpParams(
    request: GetInvUploadRequest,
    page: number,
    size: number,
    sort: string
  ): HttpParams {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort);

    if (request.createDateStart) {
      params = params.set('createDateStart', this.formatDate(request.createDateStart));
    }

    if (request.createDateEnd) {
      params = params.set('createDateEnd', this.formatDate(request.createDateEnd));
    }

    return params;
  }

  /**
   * Formats a Date object to ISO string for API consumption
   * 
   * @param date - The date to format
   * @returns ISO formatted date string (ISO 8601)
   */
  private formatDate(date: Date): string {
    return date?.toISOString() || '';
  }

  private extractFileName(res: HttpResponse<Blob>): string | null {
    const contentDisposition = res.headers.get('content-disposition');
    if (!contentDisposition) return null;
    
    // 解析 filename= 之後的字串
    const matches = /filename="?([^"]*)"?/.exec(contentDisposition);
    return matches && matches[1] ? matches[1] : null;
  }

}
